import React, { useState, useRef, DragEvent, ChangeEvent } from "react";
import {
  Box,
  Button,
  Text,
  useToast,
  VStack,
  HStack,
  Icon,
  Spinner,
} from "@chakra-ui/react";
import {
  UploadCloud,
  FileText,
  CheckCircle,
  Trash2,
} from "lucide-react";

export interface ResumeData {
  firstName?: string;
  lastName?: string;
  phoneNumber?: string;
  yearsOfExperience?: number;
  educationItems: Array<{
    schoolName: string;
    fieldOfStudy?: string;
    qualification: string;
  }>;
  employmentHistoryItems: Array<{
    companyName: string;
    jobTitle: string;
    description: string;
    startedAt: string;
    endedAt?: string;
  }>;
  projectHistoryItems: Array<{
    name: string;
    description: string;
    startedAt?: string;
    endedAt?: string;
  }>;
  certificationItems: Array<{
    name: string;
    issuerCertificationId?: string;
    issuedBy: string;
    issuedAt: string;
    url?: string;
  }>;
}

interface ResumeUploadProps {
  onUploadSuccess: (data: ResumeData) => void;
  jobId?: string;
}

export default function ResumeUpload({ onUploadSuccess, jobId }: ResumeUploadProps) {
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [file, setFile] = useState<File | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const toast = useToast();

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const processFile = async (selectedFile: File) => {
    const validTypes = [
      "application/pdf",
      "application/msword",
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    ];

    if (!validTypes.includes(selectedFile.type) && !selectedFile.name.match(/\.(pdf|doc|docx)$/i)) {
      toast({
        title: "Invalid file type",
        description: "Please upload a PDF or Word document.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    if (selectedFile.size > 5 * 1024 * 1024) {
      toast({
        title: "File too large",
        description: "Resume must be smaller than 5MB.",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      return;
    }

    setFile(selectedFile);
    await uploadAndParseResume(selectedFile);
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setIsDragging(false);

    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      processFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0) {
      processFile(e.target.files[0]);
    }
  };

  const uploadAndParseResume = async (uploadFile: File) => {
    setIsUploading(true);

    try {
      const formData = new FormData();
      formData.append("resume", uploadFile);

      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/api/v1/employer-job-application/parse-resume`,
        {
          method: "POST",
          body: formData,
        }
      );

      const result = await response.json();

      if (!response.ok) {
        throw new Error(result.error?.message || "Failed to parse resume");
      }

      if (result.success && result.data) {
        toast({
          title: "Resume parsed successfully",
          description: "We've prefilled your application form with data from your resume.",
          status: "success",
          duration: 5000,
          isClosable: true,
        });

        onUploadSuccess(result.data);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (error: any) {
      console.error("Resume parsing error:", error);
      toast({
        title: "Could not parse resume",
        description: error.message || "An unexpected error occurred. Please try again or fill the form manually.",
        status: "error",
        duration: 6000,
        isClosable: true,
      });
      setFile(null);
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const removeFile = () => {
    setFile(null);
  };

  return (
    <Box mb={8}>
      <Box
        p={6}
        borderWidth="1px"
        borderColor={isDragging ? "flatteredFlamingo.500" : "gray.300"}
        bg={isDragging ? "flatteredFlamingo.50" : "gray.50"}
        transition="all 0.2s"
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        position="relative"
      >
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileChange}
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          style={{ display: "none" }}
        />

        {!file && !isUploading ? (
          <VStack spacing={3}>
            <Icon as={UploadCloud} w={10} h={10} color="gray.400" />
            <Text
              fontSize="lg"
              fontWeight="medium"
              color="gray.700"
              textAlign="center"
            >
              Upload your resume to prefill the application
            </Text>
            <Text fontSize="sm" color="gray.500" textAlign="center">
              Drag and drop your resume here, or click to browse
            </Text>
            <Button
              mt={2}
              onClick={() => fileInputRef.current?.click()}
              colorScheme="prussianBlue"
              variant="outline"
              size="sm"
            >
              Select File
            </Button>
            <Text fontSize="xs" color="gray.400">
              Supported formats: PDF, DOC, DOCX (Max 5MB)
            </Text>
          </VStack>
        ) : isUploading ? (
          <VStack spacing={4} py={4}>
            <Spinner size="xl" color="flatteredFlamingo.500" thickness="3px" />
            <VStack spacing={1}>
              <Text fontWeight="medium" color="gray.700">
                Parsing your resume...
              </Text>
              <Text fontSize="sm" color="gray.500" textAlign="center">
                We&apos;re extracting your experience and education using AI.
                This might take a few seconds.
              </Text>
            </VStack>
          </VStack>
        ) : (
          <HStack justify="space-between" align="center" px={2}>
            <HStack spacing={4}>
              <Icon as={FileText} w={8} h={8} color="prussianBlue.500" />
              <VStack align="start" spacing={0}>
                <Text fontWeight="medium" isTruncated maxW="250px">
                  {file?.name}
                </Text>
                <HStack spacing={1} color="green.500" fontSize="sm">
                  <Icon as={CheckCircle} w={4} h={4} />
                  <Text>Successfully parsed</Text>
                </HStack>
              </VStack>
            </HStack>
            <Button
              size="sm"
              variant="ghost"
              colorScheme="red"
              onClick={removeFile}
              leftIcon={<Trash2 className="h-4 w-4" />}
            >
              Remove
            </Button>
          </HStack>
        )}
      </Box>
    </Box>
  );
}
